import { DICTATION_SILENCE_MS } from '@secretario/shared';
import { holdMic } from './micHold.js';
import { closeLegacySheet, closeTopLayer } from './layers.js';
import { isNativeShell } from './platform.js';

/**
 * Ponte com o aparelho (Capacitor). Tudo aqui tem um caminho no navegador: os
 * plugins só são carregados dentro do app instalado, sob demanda.
 */

let splashHidden = false;
/** Fecha a tela nativa de abertura (uma vez), com a saída suave de 220 ms. */
export function hideSplash(): void {
  if (splashHidden || !isNativeShell()) return;
  splashHidden = true;
  void import('@capacitor/splash-screen')
    .then(({ SplashScreen }) => SplashScreen.hide({ fadeOutDuration: 220 }))
    .catch(() => {});
}

/** Botão voltar do Android, barra de status e tela de abertura. Chamado uma vez ao abrir. */
export async function initNativeShell(): Promise<void> {
  if (!isNativeShell()) return;
  const [{ App }, { SystemBars, SystemBarsStyle }] = await Promise.all([
    import('@capacitor/app'),
    import('@capacitor/core'),
  ]);
  // Ícones escuros nas barras sobre o creme do app. O SystemBars vem no próprio
  // Capacitor 8; o plugin antigo da barra de status usava funções que a Play marca
  // como descontinuadas no Android 15.
  void SystemBars.setStyle({ style: SystemBarsStyle.Light }).catch(() => {});
  // A tela de abertura fica até o personagem estar desenhado (`hideSplash`), no
  // máximo 3 s: a cor dela é a da Início, então a troca é só o rosto aparecendo.
  setTimeout(hideSplash, 3000);
  void App.addListener('backButton', () => {
    // Painel ou cartão do palco aberto fecha primeiro, mesmo no meio da conversa ao vivo.
    if (closeTopLayer()) return;
    // Conversa ao vivo aberta: o voltar encerra a conversa (a tela trata).
    // Com uma página aberta por cima (barra curta), o voltar volta a página e a conversa segue.
    if (document.querySelector('.lv-root:not(.lv-page)')) {
      window.dispatchEvent(new CustomEvent('secretario:live-back'));
      return;
    }
    // Painel de emojis aberto: o voltar devolve a conversa (como o teclado).
    if (document.querySelector('.cx-dock.open')) {
      window.dispatchEvent(new CustomEvent('secretario:fechar-painel'));
      return;
    }
    // Camada aberta (cartão, painel, folha) fecha primeiro; depois volta na
    // navegação; na raiz, minimiza.
    if (closeLegacySheet()) return;
    if (window.location.pathname !== '/' && window.history.length > 1) window.history.back();
    else void App.minimizeApp();
  });
}

/** Toque leve ao enviar: confirma o gesto sem som. */
export function hapticTap(): void {
  if (!isNativeShell()) return;
  void import('@capacitor/haptics')
    .then(({ Haptics, ImpactStyle }) => Haptics.impact({ style: ImpactStyle.Light }))
    .catch(() => {});
}

// A tela e a fila de envios podem avisar o mesmo fato: duas vibrações iguais em menos de
// 350 ms viram uma só.
const lastFeel: Record<string, number> = {};
const repeated = (kind: string) => {
  const now = Date.now();
  const again = now - (lastFeel[kind] ?? 0) < 350;
  lastFeel[kind] = now;
  return again;
};

/** Confirmação de algo concluído (salvou, marcou como pago). */
export function hapticSuccess(): void {
  if (!isNativeShell() || repeated('success')) return;
  void import('@capacitor/haptics')
    .then(({ Haptics, NotificationType }) =>
      Haptics.notification({ type: NotificationType.Success }),
    )
    .catch(() => {});
}

/** Atenção antes de algo que não volta (apagar). */
export function hapticWarn(): void {
  if (!isNativeShell() || repeated('warn')) return;
  void import('@capacitor/haptics')
    .then(({ Haptics, NotificationType }) =>
      Haptics.notification({ type: NotificationType.Warning }),
    )
    .catch(() => {});
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error ?? new Error('leitura falhou'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Entrega um arquivo à pessoa. No navegador, baixa; no app, grava numa pasta
 * temporária e abre a folha de compartilhar do sistema (salvar, enviar, abrir).
 */
export async function saveBlob(blob: Blob, filename: string): Promise<void> {
  if (!isNativeShell()) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
    return;
  }
  const [{ Filesystem, Directory }, { Share }] = await Promise.all([
    import('@capacitor/filesystem'),
    import('@capacitor/share'),
  ]);
  const safeName = filename.replace(/[^\w.\-()À-ſ ]+/g, '_').slice(0, 80) || 'arquivo';
  const written = await Filesystem.writeFile({
    path: `compartilhar/${Date.now()}-${safeName}`,
    data: await blobToBase64(blob),
    directory: Directory.Cache,
    recursive: true,
  });
  await Share.share({ title: safeName, url: written.uri });
}

/** Compartilha um texto/link (convite). Devolve false se o aparelho não tiver a folha. */
export async function shareText(text: string, url?: string): Promise<boolean> {
  if (isNativeShell()) {
    const { Share } = await import('@capacitor/share');
    await Share.share({ text, ...(url ? { url } : {}) });
    return true;
  }
  if (typeof navigator.share === 'function') {
    await navigator.share({ text, ...(url ? { url } : {}) });
    return true;
  }
  return false;
}

export type Dictation = { stop(): void };

/** SpeechRecognizer.ERROR_RECOGNIZER_BUSY: a escuta anterior ainda não soltou. */
const RECOGNIZER_BUSY = 8;

/**
 * Ditado: no app usa o reconhecimento de fala do aparelho; no navegador, o
 * reconhecimento do próprio navegador, quando existe. Devolve null se não houver.
 *
 * A pessoa pode pensar em silêncio por até `DICTATION_SILENCE_MS` (8 s) antes de
 * a gravação encerrar sozinha: o reconhecedor do aparelho para no primeiro
 * silêncio, então ele é religado e o texto continua de onde parou. "Pronto"
 * encerra na hora.
 */
export async function startDictation(handlers: {
  onText(text: string): void;
  onEnd(): void;
  /** Segundos de silêncio que faltam para enviar sozinho (null enquanto há fala). */
  onSilence?(secondsLeft: number | null): void;
  /** Idioma do ditado (Idiomas: a aula ouve em inglês); sem ele, português. */
  language?: string;
}): Promise<Dictation | null> {
  // Texto já fechado (trechos anteriores ao religar) + o trecho que está sendo ouvido agora.
  let committed = '';
  let partialText = '';
  let lastSpeechAt = Date.now();
  let finished = false;
  let userStopped = false;
  const full = () => [committed, partialText].filter(Boolean).join(' ').trim();
  const heard = (text: string) => {
    if (text && text !== partialText) {
      partialText = text;
      lastSpeechAt = Date.now();
      handlers.onSilence?.(null);
    }
    handlers.onText(full());
  };
  const commit = () => {
    committed = full();
    partialText = '';
  };
  const silentFor = () => Date.now() - lastSpeechAt;
  const countdown = () => {
    const left = Math.ceil((DICTATION_SILENCE_MS - silentFor()) / 1000);
    if (left <= 3) handlers.onSilence?.(Math.max(0, left));
  };

  if (isNativeShell()) {
    const { SpeechRecognition } = await import('@capacitor-community/speech-recognition');
    const { available } = await SpeechRecognition.available();
    if (!available) return null;
    const permission = await SpeechRecognition.requestPermissions();
    if (permission.speechRecognition !== 'granted') return null;
    // A chamada pelo nome solta o microfone enquanto a pessoa dita.
    const releaseMic = await holdMic();
    let listening = false;
    let attempts = 0;
    let closing: ReturnType<typeof setTimeout> | undefined;
    let watchdog: ReturnType<typeof setTimeout> | undefined;
    const partial = await SpeechRecognition.addListener('partialResults', (data) => {
      const text = data.matches?.[0];
      if (text) heard(text);
    });
    // Sinais do reconhecedor (com o patch em patches/): "ready" quando o microfone abriu,
    // "started" quando a fala começa, "stopped" no fim da fala ou num erro (com o código).
    const state = await SpeechRecognition.addListener('listeningState', (raw) => {
      const data = raw as { status: string; error?: number };
      if (data.status === 'ready' || data.status === 'started') {
        listening = true;
        attempts = 0;
        clearTimeout(watchdog);
      } else if (data.status === 'stopped') {
        listening = false;
        clearTimeout(closing);
        // Reconhecedor ainda ocupado com a escuta anterior: espera um pouco e tenta de novo,
        // sem contar como silêncio da pessoa.
        if (data.error === RECOGNIZER_BUSY && !userStopped && !finished) {
          closing = setTimeout(() => {
            if (!finished) begin();
          }, 500);
          return;
        }
        // O aparelho avisa "parou" antes de entregar o fim da fala: espera um
        // instante por ele e decide se encerra ou religa para continuar ouvindo.
        closing = setTimeout(afterStop, userStopped ? 700 : 600);
      }
    });
    const ticker = setInterval(() => {
      if (finished) return;
      countdown();
      if (!userStopped && silentFor() >= DICTATION_SILENCE_MS) {
        userStopped = true;
        void SpeechRecognition.stop().catch(() => {});
        clearTimeout(closing);
        closing = setTimeout(afterStop, listening ? 700 : 0);
      }
    }, 250);
    const afterStop = () => {
      if (finished) return;
      if (userStopped || silentFor() >= DICTATION_SILENCE_MS) {
        finish();
        return;
      }
      // Ainda dentro do tempo de silêncio: guarda o trecho e volta a ouvir.
      commit();
      begin();
    };
    const finish = () => {
      if (finished) return;
      finished = true;
      clearInterval(ticker);
      clearTimeout(closing);
      clearTimeout(watchdog);
      void partial.remove();
      void state.remove();
      // Solta o reconhecedor: a próxima escuta começa limpa.
      void SpeechRecognition.stop().catch(() => {});
      releaseMic();
      handlers.onEnd();
    };
    // Às vezes o aparelho aceita o pedido e não abre o microfone (o reconhecedor
    // anterior ainda está ocupado). Sem o "pronto" em pouco tempo, tenta de novo; se
    // ainda assim não abrir, encerra em vez de deixar a tela "ouvindo" para sempre.
    // O sinal é o "pronto" do microfone, não o começo da fala: antes, quem demorava
    // mais de 2 s para começar a falar tinha o reconhecedor religado no meio da frase.
    const begin = (): void => {
      if (finished) return;
      attempts += 1;
      listening = false;
      clearTimeout(watchdog);
      watchdog = setTimeout(() => {
        if (finished || listening) return;
        if (attempts >= 3) {
          finish();
          return;
        }
        void SpeechRecognition.stop()
          .catch(() => {})
          .then(() => new Promise((resolve) => setTimeout(resolve, 350)))
          .then(() => {
            if (!finished) begin();
          });
      }, 2500);
      void SpeechRecognition.start({
        language: handlers.language ?? 'pt-BR',
        partialResults: true,
        popup: false,
      }).catch(() => {
        if (!finished && !userStopped && attempts < 3) setTimeout(begin, 400);
        else finish();
      });
    };
    begin();
    return {
      stop: () => {
        // "Pronto": pede para parar e espera o trecho final chegar.
        userStopped = true;
        void SpeechRecognition.stop().catch(() => {});
        clearTimeout(closing);
        closing = setTimeout(afterStop, listening ? 700 : 0);
      },
    };
  }

  type WebRecognition = {
    lang: string;
    interimResults: boolean;
    continuous: boolean;
    start(): void;
    stop(): void;
    onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
    onend: (() => void) | null;
    onerror: (() => void) | null;
  };
  const scope = window as unknown as {
    SpeechRecognition?: new () => WebRecognition;
    webkitSpeechRecognition?: new () => WebRecognition;
  };
  const Ctor = scope.SpeechRecognition ?? scope.webkitSpeechRecognition;
  if (!Ctor) return null;
  let recognition: WebRecognition | null = null;
  const done = () => {
    if (finished) return;
    finished = true;
    clearInterval(ticker);
    handlers.onEnd();
  };
  const listen = () => {
    const current = new Ctor();
    recognition = current;
    current.lang = 'pt-BR';
    current.interimResults = true;
    current.continuous = true;
    current.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i += 1) {
        transcript += event.results[i]?.[0]?.transcript ?? '';
      }
      heard(transcript.trim());
    };
    // O navegador também para sozinho às vezes: dentro do tempo de silêncio, volta a ouvir.
    current.onend = () => {
      if (userStopped || silentFor() >= DICTATION_SILENCE_MS) {
        done();
        return;
      }
      commit();
      try {
        listen();
      } catch {
        done();
      }
    };
    current.onerror = () => {
      userStopped = true;
    };
    current.start();
  };
  const ticker = setInterval(() => {
    if (finished) return;
    countdown();
    if (!userStopped && silentFor() >= DICTATION_SILENCE_MS) {
      userStopped = true;
      recognition?.stop();
    }
  }, 250);
  listen();
  return {
    stop: () => {
      userStopped = true;
      recognition?.stop();
    },
  };
}

/** Há alguma forma de ditado neste aparelho? (para decidir se o microfone aparece) */
export function canDictate(): boolean {
  if (isNativeShell()) return true;
  const scope = window as unknown as {
    SpeechRecognition?: unknown;
    webkitSpeechRecognition?: unknown;
  };
  return !!(scope.SpeechRecognition ?? scope.webkitSpeechRecognition);
}

export type DeviceContact = { displayName: string; phone: string };

/**
 * Abre a página deste app nas configurações do aparelho (onde ficam as
 * permissões). Devolve false quando não há como abrir.
 */
export async function openAppSettings(): Promise<boolean> {
  if (!isNativeShell()) return false;
  try {
    const { NativeSettings, AndroidSettings, IOSSettings } =
      await import('capacitor-native-settings');
    await NativeSettings.open({
      optionAndroid: AndroidSettings.ApplicationDetails,
      optionIOS: IOSSettings.App,
    });
    return true;
  } catch {
    return false;
  }
}

/** A agenda do aparelho já está liberada para o app? (sem abrir pedido) */
export async function contactsAllowed(): Promise<boolean> {
  if (!isNativeShell()) return false;
  try {
    const { Contacts } = await import('@capacitor-community/contacts');
    return (await Contacts.checkPermissions()).contacts === 'granted';
  } catch {
    return false;
  }
}

/**
 * Contatos do aparelho, com permissão pedida na hora e só quando a pessoa toca
 * em importar. Devolve null se ela negar; nada é enviado sem confirmação.
 * Falha de leitura (que não é recusa) sobe como erro.
 */
export async function readDeviceContacts(): Promise<DeviceContact[] | null> {
  if (!isNativeShell()) return null;
  const { Contacts } = await import('@capacitor-community/contacts');
  let state = (await Contacts.checkPermissions()).contacts;
  if (state !== 'granted') state = (await Contacts.requestPermissions()).contacts;
  if (state !== 'granted') return null;
  const { contacts } = await Contacts.getContacts({ projection: { name: true, phones: true } });
  const seen = new Set<string>();
  const result: DeviceContact[] = [];
  for (const contact of contacts) {
    // De quem tem vários números, vem o celular; sem celular marcado, o primeiro válido.
    const phones = (contact.phones ?? [])
      .map((entry) => ({ type: entry.type, number: entry.number?.trim() ?? '' }))
      .filter((entry) => entry.number.replace(/\D/g, '').length >= 8);
    const phone = (phones.find((entry) => String(entry.type) === 'mobile') ?? phones[0])?.number;
    if (!phone) continue;
    const digits = phone.replace(/\D/g, '');
    if (seen.has(digits)) continue;
    seen.add(digits);
    // Sem nome na agenda, entra com o próprio número.
    const name = (contact.name?.display ?? '').replace(/\s+/g, ' ').trim();
    result.push({
      displayName: Array.from(name || phone)
        .slice(0, 80)
        .join(''),
      phone: phone.slice(0, 40),
    });
  }
  return result.sort((a, b) => a.displayName.localeCompare(b.displayName, 'pt-BR'));
}

/**
 * Os nomes da agenda do aparelho por número (os 8 últimos dígitos), só quando a pessoa já
 * deixou ver os contatos: não pede permissão. Fica no aparelho; serve para reconhecer quem
 * escreveu num aviso que mostra o nome salvo, e não o número. Sem permissão, devolve null.
 */
export async function deviceContactNames(): Promise<Map<string, string[]> | null> {
  if (!(await contactsAllowed())) return null;
  try {
    const { Contacts } = await import('@capacitor-community/contacts');
    const { contacts } = await Contacts.getContacts({ projection: { name: true, phones: true } });
    const byPhone = new Map<string, string[]>();
    for (const contact of contacts) {
      const name = (contact.name?.display ?? '').replace(/\s+/g, ' ').trim();
      if (!name) continue;
      for (const entry of contact.phones ?? []) {
        const digits = (entry.number ?? '').replace(/\D/g, '');
        if (digits.length < 8) continue;
        const key = digits.slice(-8);
        byPhone.set(key, [...(byPhone.get(key) ?? []), name]);
      }
    }
    return byPhone;
  } catch {
    return null;
  }
}

async function onAndroid(): Promise<boolean> {
  if (!isNativeShell()) return false;
  const { Capacitor } = await import('@capacitor/core');
  return Capacitor.getPlatform() === 'android';
}

/**
 * Espera o SMS do código chegar e devolve os 6 números. No Android o sistema
 * mostra um pedido de um toque ("permitir ler esta mensagem?"); o app não tem
 * permissão de SMS e não lê mais nada. No iPhone o teclado já sugere o código.
 * Devolve null quando não há como ler (outro aparelho, recusa ou tempo esgotado).
 */
export async function waitForSmsCode(): Promise<string | null> {
  try {
    if (!(await onAndroid())) return null;
    const { AndroidSmsRetriever } = await import('@capawesome/capacitor-android-sms-retriever');
    const { message } = await AndroidSmsRetriever.retrieveSms();
    return message.match(/\b\d{6}\b/)?.[0] ?? null;
  } catch {
    return null;
  }
}

/** O Android oferece o número do próprio aparelho (um toque, sem permissão). */
export async function canSuggestDevicePhone(): Promise<boolean> {
  try {
    return await onAndroid();
  } catch {
    return false;
  }
}

export async function suggestDevicePhone(): Promise<string | null> {
  try {
    if (!(await onAndroid())) return null;
    const { AndroidSmsRetriever } = await import('@capawesome/capacitor-android-sms-retriever');
    const { phoneNumber } = await AndroidSmsRetriever.requestPhoneNumber();
    return phoneNumber || null;
  } catch {
    return null;
  }
}
